/**
 * Transport for the user routes of the `appProxy` HTTP router (T-330, contract app-proxy-web
 * §2–§4): one request per call to its app routes (/app/**). Errors are thrown in the shape of
 * the SDK's FunctionsError (`functions/<code>`, `message`, `details`), like adminProxy.ts,
 * because naraDoor.ts and the pages read them that way. A successful write (any method but GET)
 * refreshes the cache like lib/callable.ts (T-239). `callAuth` is the same transport for the web's
 * identity-writer routes on `authProxy` (/auth/**, T-330 batch 8), which runs as hub-auth.
 */

import { refreshAfterWrite } from "./callable";
import { auth } from "./firebase";
import { getCurrentWorkspaceId } from "./workspaceStore";

export type AppMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

// The base URL: the direct function URL, because Hosting cuts a rewritten request at 60 s.
// Read at call time so a test (or the emulator switch) can change it after import.
function functionUrl(fn: string): string {
  const project = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  const base = import.meta.env.DEV
    ? `http://127.0.0.1:5001/${project}/us-east4`
    : `https://us-east4-${project}.cloudfunctions.net`;
  return `${base}/${fn}`;
}

export const appProxyUrl = (path: string) => functionUrl(`appProxy/app${path}`);
export const authProxyUrl = (path: string) => functionUrl(`authProxy/auth${path}`);

const proxyError = (code: string, message: string, details: unknown = null) =>
  Object.assign(new Error(message), { code: `functions/${code}`, details });

function parseJson(text: string): { ok: true; value: unknown } | { ok: false } {
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch {
    return { ok: false };
  }
}

/** The envelope `{ error, code, details? }` of a failed call, or the error to throw when the body is not one. */
function envelopeError(value: unknown): Error {
  const { error, code, details } = (value ?? {}) as { error?: unknown; code?: unknown; details?: unknown };
  if (typeof code !== "string" || typeof error !== "string") return proxyError("internal", "Unexpected server response");
  return proxyError(code, error, details ?? null);
}

/**
 * `method` `/app<path>` with the caller's ID token and, when there is one, the workspace header
 * (the current workspace unless one is given). Resolves to the response JSON (null for an empty body).
 */
export function callApp<R>(
  method: AppMethod,
  path: string,
  body?: unknown,
  workspaceId: string | null = getCurrentWorkspaceId()
): Promise<R> {
  return send<R>("appProxy", appProxyUrl(path), method, path, body, workspaceId, method !== "GET");
}

/** `method` `/auth<path>` with the caller's ID token; no workspace header (authProxy's CORS allows none). */
export function callAuth<R>(method: "GET" | "POST", path: string, body?: unknown, refresh = method !== "GET"): Promise<R> {
  return send<R>("authProxy", authProxyUrl(path), method, path, body, null, refresh);
}

async function send<R>(
  proxy: string,
  url: string,
  method: AppMethod,
  path: string,
  body: unknown,
  workspaceId: string | null,
  refresh: boolean
): Promise<R> {
  const token = await auth.currentUser?.getIdToken();
  const headers: Record<string, string> = {
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(workspaceId && { "X-NaraWorkspace-ID": workspaceId }),
    ...(body !== undefined && { "Content-Type": "application/json" }),
  };
  // Ids in the path are opaque; the query string is dropped from the log.
  const fail = (error: Error, status?: number) => {
    console.error(`${proxy} call failed`, { method, path: path.split("?")[0], status, code: callCode(error) });
    return error;
  };

  let response: Response;
  try {
    response = await fetch(url, { method, headers, ...(body !== undefined && { body: JSON.stringify(body) }) });
  } catch {
    throw fail(proxyError("unavailable", "Could not reach the server"));
  }

  const text = await response.text();
  const parsed = parseJson(response.ok && text === "" ? "null" : text);
  if (response.ok && parsed.ok) {
    if (refresh) await refreshAfterWrite();
    return parsed.value as R;
  }
  throw fail(parsed.ok ? envelopeError(parsed.value) : proxyError("internal", "Unexpected server response"), response.status);
}

const callCode = (error: Error) => (error as Error & { code: string }).code.replace(/^functions\//, "");
